import type {
  BlendPointRef,
  BlendPartialCondition,
  ChamferFeatureDefinition,
  FilletFeatureDefinition,
  PartialEdgeParam,
} from '@puppycad/cad-core';
import { createDefaultPartialEdgeParam } from './stage-two';

/**
 * 部分边线参考选择目标。
 */
export interface BlendPartialReferenceTarget {
  /** 目标边引用。 */
  itemRef: string;
  /** 起点或终点侧。 */
  side: 'start' | 'end';
}

/**
 * 部分边线参考目标激活结果。
 */
export interface PartialReferenceActivationResult<TSelectionTarget extends string> {
  /** 下一次参考选择目标。 */
  referenceTarget: BlendPartialReferenceTarget | null;
  /** 下一次主选择槽。 */
  selectionTarget: TSelectionTarget;
  /** 是否需要同步视口选择模式。 */
  shouldSyncSelectionMode: boolean;
}

/**
 * 可承载部分边线参数的定义。
 */
type PartialEdgeCapableDefinition = FilletFeatureDefinition | ChamferFeatureDefinition;

/**
 * 按主选择项同步部分边线参数占位。
 *
 * @param current 当前参数集合。
 * @param itemRefs 当前边引用集合。
 * @returns 与边引用一一对应的部分边线参数集合。
 */
export function ensurePartialEdgeParams(
  current: PartialEdgeParam[] | undefined,
  itemRefs: string[],
): PartialEdgeParam[] {
  return itemRefs.map((itemRef) => (
    current?.find((candidate) => candidate.itemRef === itemRef)
    ?? createDefaultPartialEdgeParam(itemRef)
  ));
}

/**
 * 判断参考选择目标是否指向指定字段。
 *
 * @param target 当前参考目标。
 * @param itemRef 边引用。
 * @param side 起止侧。
 * @returns 是否命中。
 */
export function isSamePartialReferenceTarget(
  target: BlendPartialReferenceTarget | null,
  itemRef: string,
  side: 'start' | 'end',
): boolean {
  return target?.itemRef === itemRef && target.side === side;
}

/**
 * 根据部分边线条件变化解析下一次参考选择激活状态。
 *
 * @param input 当前条件变化上下文。
 * @returns 下一次参考选择状态。
 */
export function resolvePartialReferenceActivationAfterConditionChange<TSelectionTarget extends string>(input: {
  /** 当前激活的参考目标。 */
  currentTarget: BlendPartialReferenceTarget | null;
  /** 当前边引用。 */
  itemRef: string;
  /** 起止侧。 */
  side: 'start' | 'end';
  /** 新条件。 */
  condition: BlendPartialCondition;
  /** 参考选择槽。 */
  referenceSelectionTarget: TSelectionTarget;
  /** 默认选择槽。 */
  defaultSelectionTarget: TSelectionTarget;
}): PartialReferenceActivationResult<TSelectionTarget> {
  if (input.condition === 'reference-offset') {
    return {
      referenceTarget: {
        itemRef: input.itemRef,
        side: input.side,
      },
      selectionTarget: input.referenceSelectionTarget,
      shouldSyncSelectionMode: true,
    };
  }
  if (isSamePartialReferenceTarget(input.currentTarget, input.itemRef, input.side)) {
    return {
      referenceTarget: null,
      selectionTarget: input.defaultSelectionTarget,
      shouldSyncSelectionMode: true,
    };
  }
  return {
    referenceTarget: input.currentTarget,
    selectionTarget: input.defaultSelectionTarget,
    shouldSyncSelectionMode: false,
  };
}

/**
 * 切换部分边线参数启用状态。
 *
 * @param definition 当前定义。
 * @param itemRefs 当前边引用集合。
 * @param enabled 是否启用。
 * @returns 新定义。
 */
export function setPartialEdgeParametersEnabled<TDefinition extends PartialEdgeCapableDefinition>(
  definition: TDefinition,
  itemRefs: string[],
  enabled: boolean,
): TDefinition {
  return {
    ...definition,
    partialEdgeParametersEnabled: enabled,
    /**
     * 启用时先补齐占位参数，关闭时保留旧值供再次启用恢复；solver 会通过启用开关统一过滤。
     */
    partialEdges: enabled
      ? ensurePartialEdgeParams(definition.partialEdges, itemRefs)
      : definition.partialEdges,
  };
}

/**
 * 更新部分边线条件。
 *
 * @param definition 当前定义。
 * @param itemRef 边引用。
 * @param side 起止侧。
 * @param condition 目标条件。
 * @returns 新定义。
 */
export function updatePartialEdgeCondition<TDefinition extends PartialEdgeCapableDefinition>(
  definition: TDefinition,
  itemRef: string,
  side: 'start' | 'end',
  condition: BlendPartialCondition,
): TDefinition {
  return {
    ...definition,
    partialEdges: ensurePartialEdgeParams(
      definition.partialEdges,
      (definition.items ?? []).map((item) => item.persistentRef),
    ).map((item) => {
      if (item.itemRef !== itemRef) {
        return item;
      }
      if (side === 'start') {
        return {
          ...item,
          startCondition: condition,
          startReferencePoint: condition === 'reference-offset' ? item.startReferencePoint : null,
          startValue: condition === 'reference-offset' ? undefined : item.startValue,
          startExpression: condition === 'reference-offset' ? null : item.startExpression,
        };
      }
      return {
        ...item,
        endCondition: condition,
        endReferencePoint: condition === 'reference-offset' ? item.endReferencePoint : null,
        endValue: condition === 'reference-offset' ? undefined : item.endValue,
        endExpression: condition === 'reference-offset' ? null : item.endExpression,
      };
    }),
  };
}

/**
 * 更新部分边线数值。
 *
 * @param definition 当前定义。
 * @param itemRef 边引用。
 * @param side 起止侧。
 * @param value 目标数值。
 * @param expression 表达式。
 * @returns 新定义。
 */
export function updatePartialEdgeValue<TDefinition extends PartialEdgeCapableDefinition>(
  definition: TDefinition,
  itemRef: string,
  side: 'start' | 'end',
  value: number,
  expression: string | null,
): TDefinition {
  return {
    ...definition,
    partialEdges: (definition.partialEdges ?? []).map((item) => {
      if (item.itemRef !== itemRef) {
        return item;
      }
      return side === 'start'
        ? { ...item, startValue: value, startExpression: expression }
        : { ...item, endValue: value, endExpression: expression };
    }),
  };
}

/**
 * 写入部分边线参考点。
 *
 * @param definition 当前定义。
 * @param target 参考目标。
 * @param referencePoint 参考点。
 * @returns 新定义。
 */
export function updatePartialEdgeReference<TDefinition extends PartialEdgeCapableDefinition>(
  definition: TDefinition,
  target: BlendPartialReferenceTarget,
  referencePoint: BlendPointRef,
): TDefinition {
  return {
    ...definition,
    partialEdges: (definition.partialEdges ?? []).map((item) => {
      if (item.itemRef !== target.itemRef) {
        return item;
      }
      return target.side === 'start'
        ? {
          ...item,
          startCondition: 'reference-offset',
          startValue: undefined,
          startExpression: null,
          startReferencePoint: referencePoint,
        }
        : {
          ...item,
          endCondition: 'reference-offset',
          endValue: undefined,
          endExpression: null,
          endReferencePoint: referencePoint,
        };
    }),
  };
}

/**
 * 清空部分边线参考项。
 *
 * @param definition 当前定义。
 * @param itemRef 边引用。
 * @param side 起止侧。
 * @returns 新定义。
 */
export function clearPartialEdgeReference<TDefinition extends PartialEdgeCapableDefinition>(
  definition: TDefinition,
  itemRef: string,
  side: 'start' | 'end',
): TDefinition {
  return {
    ...definition,
    partialEdges: (definition.partialEdges ?? []).map((item) => {
      if (item.itemRef !== itemRef) {
        return item;
      }
      return side === 'start'
        ? { ...item, startReferencePoint: null }
        : { ...item, endReferencePoint: null };
    }),
  };
}
